import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingBasket, faCubes } from '@fortawesome/free-solid-svg-icons';
import Table from '../Listing/Table';
import {
    productIdColumn, productNameColumn, productPriceColumn, productStockColumn,
    productStatusColumn, productRefundableColumn,
} from './productColumns';
import './ProductEquivalents.scss';

// Only the computed fallback is explained; lists coming from medicament.ma show no source line.
const SOURCE_LABELS = {
    computed: 'Calculé à partir de la composition, du dosage et de la forme',
};

// Table for the product page's "Similaires" / "Même DCI" sections (see ProductEquivalenceService).
// `referencePrice`: when given (Similaires), the price column also shows each row's gap with the
// product being viewed — the usual reason to look up an equivalent is offering a cheaper one.
const ProductEquivalents = ({
    products = [], loading, emptyMessage, referencePrice, totalCount, source,
    onAddToCart, addingToCartId, onViewStock,
}) => {
    const { t, i18n } = useTranslation();
    const isArabic = i18n.language === 'ar';

    // Same cells as the products listing (productColumns.js); Similaires also shows each row's
    // price gap with the product being viewed.
    const columns = [
        productIdColumn(),
        productNameColumn({ t, isArabic }),
        productPriceColumn({ t, referencePrice }),
        // client-side sort reads row[sortField] — stockCount is a server-only sort key
        { ...productStockColumn({ t }), sortField: 'totalStock' },
        productStatusColumn({ t }),
        productRefundableColumn({ t }),
        {
            Header: '',
            accessor: 'actions',
            className: 'col-actions',
            Cell: (row) => (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                    <button
                        className="cell-actions-btn"
                        data-tooltip-id="tooltip"
                        data-tooltip-content="Ajouter au panier"
                        onClick={() => onAddToCart?.(row.id, row.totalStock)}
                        disabled={addingToCartId === row.id}
                    >
                        {addingToCartId === row.id
                            ? <span className="spinner-border spinner-border-sm" />
                            : <FontAwesomeIcon icon={faShoppingBasket} />}
                    </button>
                    {onViewStock && (
                        <button
                            className="cell-actions-btn"
                            data-tooltip-id="tooltip"
                            data-tooltip-content="Voir les lots"
                            onClick={() => onViewStock(row.id, row.name)}
                        >
                            <FontAwesomeIcon icon={faCubes} />
                        </button>
                    )}
                </div>
            ),
        },
    ];

    return (
        <div className="peq">
            {/* pageSize = all rows: client mode loads more on scrolling the table's own container,
                which never scrolls here (the accordion isn't height-bound) */}
            <Table
                mode="client"
                rowId="id"
                columns={columns}
                data={products}
                loading={loading}
                pageSize={10}
                pagination
                emptyMessage={emptyMessage}
                searchPlaceholder={t('Search a product...')}
                showExportButton={false}
                showViewsButton={false}
            />
            {totalCount > products.length && (
                <div className="peq-more">{products.length} affichés sur {totalCount}</div>
            )}
            {!loading && SOURCE_LABELS[source] && <div className="peq-source">{SOURCE_LABELS[source]}</div>}
        </div>
    );
};

export default ProductEquivalents;
